import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase/app";
import {
  isMembershipCurrentlyActive,
  membershipId,
  resolvedLeagueID,
  ROLE_SORT_RANK,
  USER_ROLE_LABELS,
  type SBotActiveContext,
  type SBotMembership,
  type SBotMembershipTargetType,
  type SBotUser,
  type SBotUserRole,
} from "@/models/user";
import { trimmedNonEmpty } from "@/models/firestore";

// Port of Services/SBotWorkspaceContextService.swift.

export interface SBotWorkspaceOption {
  membershipID: string;
  targetType: SBotMembershipTargetType;
  targetID: string;
  role: SBotUserRole;
  roleLabel: string;
  displayTitle: string;
  subtitle: string;
  leagueID?: string;
  sportID?: string;
  organizationID?: string;
  activeContext: SBotActiveContext;
}

export function isLeagueManagementOption(option: SBotWorkspaceOption): boolean {
  return option.targetType === "league" && (option.role === "admin" || option.role === "leagueManager");
}

function displayRoleLabel(membership: SBotMembership): string {
  if (membership.targetType === "organization" && membership.role === "admin") {
    return "Admin";
  }
  return USER_ROLE_LABELS[membership.role];
}

function optionForMembership(membership: SBotMembership, leagueNamesByID: Record<string, string>): SBotWorkspaceOption {
  const leagueID = resolvedLeagueID(membership);
  const leagueName = leagueID ? trimmedNonEmpty(leagueNamesByID[leagueID]) : undefined;
  const fallbackLeagueName = membership.targetType === "league" ? membership.displayName : leagueID;
  const resolvedLeagueName = leagueName ?? trimmedNonEmpty(fallbackLeagueName) ?? "League";
  const roleLabel = displayRoleLabel(membership);
  const contextDisplayName = membership.targetType === "league" ? resolvedLeagueName : membership.displayName;
  const organizationID = membership.targetType === "organization" ? (membership.organizationID ?? membership.targetID) : undefined;

  const displayTitle =
    membership.targetType === "league"
      ? `${resolvedLeagueName} ${roleLabel}`
      : `${resolvedLeagueName} > ${membership.displayName} ${roleLabel}`;
  const subtitle =
    membership.targetType === "league" ? "League workspace" : `${membership.displayName} in ${resolvedLeagueName}`;

  return {
    membershipID: membershipId(membership),
    targetType: membership.targetType,
    targetID: membership.targetID,
    role: membership.role,
    roleLabel,
    displayTitle,
    subtitle,
    leagueID,
    sportID: membership.sportID,
    organizationID,
    activeContext: {
      targetType: membership.targetType,
      targetID: membership.targetID,
      displayName: contextDisplayName,
      sportID: membership.sportID,
      leagueID,
      organizationID,
      role: membership.role,
    },
  };
}

const TARGET_RANK: Record<SBotMembershipTargetType, number> = { league: 0, organization: 1 };

function workspaceSort(lhs: SBotWorkspaceOption, rhs: SBotWorkspaceOption): number {
  const lhsLeague = lhs.leagueID ?? lhs.displayTitle;
  const rhsLeague = rhs.leagueID ?? rhs.displayTitle;
  const leagueOrder = lhsLeague.localeCompare(rhsLeague, undefined, { sensitivity: "base" });
  if (leagueOrder !== 0) return leagueOrder;
  if (TARGET_RANK[lhs.targetType] !== TARGET_RANK[rhs.targetType]) {
    return TARGET_RANK[lhs.targetType] - TARGET_RANK[rhs.targetType];
  }
  if (ROLE_SORT_RANK[lhs.role] !== ROLE_SORT_RANK[rhs.role]) {
    return ROLE_SORT_RANK[lhs.role] - ROLE_SORT_RANK[rhs.role];
  }
  return lhs.displayTitle.localeCompare(rhs.displayTitle, undefined, { sensitivity: "base" });
}

export function workspaceOptionsSync(user: SBotUser, leagueNamesByID: Record<string, string> = {}): SBotWorkspaceOption[] {
  return user.memberships
    .filter((m) => isMembershipCurrentlyActive(m))
    .map((m) => optionForMembership(m, leagueNamesByID))
    .sort(workspaceSort);
}

export function selectedWorkspaceOption(user: SBotUser | null, options: SBotWorkspaceOption[]): SBotWorkspaceOption | undefined {
  if (!user) return undefined;
  const context = user.activeContext;
  if (context) {
    const active = options.find(
      (o) => o.targetType === context.targetType && o.targetID === context.targetID && o.role === context.role,
    );
    if (active) return active;
  }
  return options[0];
}

async function leagueNamesByID(user: SBotUser): Promise<Record<string, string>> {
  const leagueIDs = new Set(
    user.memberships
      .filter((membership) => isMembershipCurrentlyActive(membership))
      .map(resolvedLeagueID)
      .filter((id): id is string => !!id),
  );
  const names: Record<string, string> = {};
  await Promise.all(
    [...leagueIDs].map(async (leagueID) => {
      try {
        const snapshot = await getDoc(doc(db, "leagues", leagueID));
        const name = trimmedNonEmpty(snapshot.data()?.name as string | undefined);
        if (name) names[leagueID] = name;
      } catch {
        // League names are display sugar; ignore fetch failures.
      }
    }),
  );
  return names;
}

export async function workspaceOptions(user: SBotUser | null): Promise<SBotWorkspaceOption[]> {
  if (!user) return [];
  const names = await leagueNamesByID(user);
  return workspaceOptionsSync(user, names);
}
